import { jwtDecode } from "jwt-decode";
import { createContext, useContext, type ReactNode, useEffect, useState, useRef } from "react";

import { LoginResponse } from "../../generated/openapi-codegen/ob-software/station-display/station-display-auth/models/LoginResponse";
import { AUTH_TOKEN_KEY } from "./constants";
import { getBearerToken } from "./getAuthTokens";
import { urls } from "../../generated/openapi-codegen/ob-software/station-display/station-display-auth/apis/StationDisplayAuthApi";

type AccountProviderProps = {
	children: ReactNode;
};

type TokenData = {
	usr: User;
};

type User = {
	email: string;
	firstName: string;
	lastName: string;
	username: string;
	roles: Array<string>;
	offices: Array<string>;
};

export type AccountContextType = {
	isAuthenticated: boolean;
	hasPasswordToReset: boolean;
	getBearerToken: () => string;
	refreshToken: () => Promise<string | null>;
	getUser: () => User | null;
	getRoles: () => Array<string>;
	logout: () => void;
	onLoginSuccess: (token: LoginResponse) => void;
};

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider = ({ children }: AccountProviderProps) => {
	const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
	const [isAuthenticated, setIsAuthenticated] = useState(!!token);

	let initialHasPasswordToReset = false;
	if (token) {
		const tokenData = JSON.parse(token) as LoginResponse;
		initialHasPasswordToReset = !!tokenData.password_to_reset;
	}

	const [hasPasswordToReset, setHasPasswordToReset] = useState(initialHasPasswordToReset);

	const channelRef = useRef<BroadcastChannel | null>(null);

	useEffect(() => {
		channelRef.current = new BroadcastChannel("account");

		const handleMessage = (event: MessageEvent) => {
			if (event.data) {
				if (event.data.command === "login") {
					setIsAuthenticated(true);
					// Update hasPasswordToReset from localStorage when login message is received
					const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
					if (token) {
						const tokenData = JSON.parse(token) as LoginResponse;
						setHasPasswordToReset(!!tokenData.password_to_reset);
					}
				}
				if (event.data.command === "logout") {
					setIsAuthenticated(false);
					setHasPasswordToReset(false);
				}
			}
		};

		channelRef.current.addEventListener("message", handleMessage);

		return () => {
			if (channelRef.current) {
				channelRef.current.removeEventListener("message", handleMessage);
				channelRef.current.close();
				channelRef.current = null;
			}
		};
	}, []);

	const getRoles = () => {
		const user = getUser();
		return user ? user.roles : [];
	};

	const logout = () => {
		fetch(urls.logout, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: getBearerToken(),
			},
			credentials: "include",
		}).finally(() => {
			window.localStorage.removeItem(AUTH_TOKEN_KEY);
		});
		setIsAuthenticated(false);
		setHasPasswordToReset(false);

		if (channelRef.current) {
			channelRef.current.postMessage({ command: "logout" });
		}
	};

	const onLoginSuccess = (token: LoginResponse) => {
		window.localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(token));
		setIsAuthenticated(true);
		setHasPasswordToReset(!!token.password_to_reset);

		if (channelRef.current) {
			channelRef.current.postMessage({ command: "login" });
		}
	};

	async function refreshToken(): Promise<string | null> {
		try {
			const response = await fetch(urls.loginByRefreshToken, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
			});

			if (!response.ok) {
				logout();

				console.error("Refresh token failed:");
				return null;
			}

			const token = await response.json();

			onLoginSuccess(token);

			return token.access_token;
		} catch (error) {
			logout();
			console.error("Refresh token failed:", error);
			return null;
		}
	}

	const getUser = () => {
		const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
		if (!token) return null;
		const tokenData = jwtDecode(JSON.parse(token).access_token) as TokenData;

		return {
			roles: tokenData.usr.roles,
			email: tokenData.usr.email,
			firstName: tokenData.usr.firstName,
			lastName: tokenData.usr.lastName,
			username: tokenData.usr.username,
			offices: tokenData.usr.offices || [],
		};
	};

	const value: AccountContextType = {
		getRoles,
		onLoginSuccess,
		logout,
		getUser,
		hasPasswordToReset,
		isAuthenticated,
		getBearerToken,
		refreshToken,
	};

	return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = () => {
	const context = useContext(AccountContext);
	if (!context) {
		throw new Error("useAccount must be used within an AccountProvider");
	}
	return context;
};
